import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Sparkles, StickyNote, Bell } from "lucide-react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { PatientDetails, PatientIntakeProfile, RecommendationsCard, getPatientAge, listPatientNotes } from "../../../patient";
import type { Patient } from "../../../patient";
import PatientAgreementsSummaryCard from "./PatientAgreementsSummaryCard";
import PatientNextSession from "./PatientNextSession";
import { useSubscription } from "../../../subscription";
import PatientCustomFields from "./PatientCustomFields";
import { listPatientSessions } from "../../application/sessions";
import type { TriggeredAlert } from "../../../session";

interface PatientViewOverviewProps {
    patient: Patient;
    onRefresh: () => void;
    onTabChange: (tab: string) => void;
    onDelete: () => void;
    deleting: boolean;
}

interface AlertCounts {
    critical: number;
    medium: number;
    low: number;
}

function AlertTypesPieCard({ patientId, onViewAll }: { patientId: string; onViewAll: () => void }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState<AlertCounts>({ critical: 0, medium: 0, low: 0 });
    const [total, setTotal] = useState(0);

    useEffect(() => {
        listPatientSessions(patientId, { limit: 100 })
            .then((data) => {
                const sessions = data.sessions ?? [];
                const c: AlertCounts = { critical: 0, medium: 0, low: 0 };
                sessions.forEach((s) => {
                    (s.triggered_alerts as TriggeredAlert[] ?? []).forEach((a) => {
                        const lower = (a.criticality ?? "").toLowerCase();
                        if (lower.includes("crítica") || lower.includes("critical")) c.critical++;
                        else if (lower.includes("media") || lower.includes("medium")) c.medium++;
                        else c.low++;
                    });
                });
                setCounts(c);
                setTotal(c.critical + c.medium + c.low);
            })
            .finally(() => setLoading(false));
    }, [patientId]);

    const series = [counts.critical, counts.medium, counts.low].filter((_, i) => [counts.critical, counts.medium, counts.low][i] > 0);
    const labels = [t("patient.alerts.critical", "Críticas"), t("patient.alerts.medium", "Medias"), t("patient.alerts.low", "Bajas")].filter((_, i) => [counts.critical, counts.medium, counts.low][i] > 0);
    const colors = ["#f87171", "#fbbf24", "#7592ff"].filter((_, i) => [counts.critical, counts.medium, counts.low][i] > 0);

    const options: ApexOptions = {
        chart: { type: "donut", fontFamily: "inherit", animations: { enabled: true, speed: 600 }, toolbar: { show: false } },
        labels,
        colors,
        stroke: { show: false },
        dataLabels: { enabled: false },
        legend: { show: false },
        plotOptions: {
            pie: {
                expandOnClick: false,
                donut: {
                    size: "78%",
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: t("patient.alerts.total", "Alertas"),
                            color: "#94a3b8",
                            fontSize: "11px",
                            formatter: () => String(total),
                        },
                        value: { show: false },
                        name: { show: true, fontSize: "10px", color: "#94a3b8", offsetY: 18 },
                    },
                },
            },
        },
        tooltip: { enabled: true, theme: "light", style: { fontSize: "11px" } },
    };

    return (
        <div className="glass-effect glass-border border rounded-2xl p-5 shadow-theme-sm flex flex-col">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                        <Bell className="w-4 h-4 text-red-500 dark:text-red-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {t("patient.alertTypes", "Tipos de alerta")}
                    </h3>
                </div>
                <button
                    onClick={onViewAll}
                    className="flex items-center gap-0.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:gap-1 transition-all"
                >
                    {t("common.viewAll")}
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center py-4">
                    <div className="h-24 w-24 bg-gray-100 dark:bg-white/5 rounded-full animate-pulse" />
                </div>
            ) : total === 0 ? (
                <p className="text-sm text-content-tertiary italic py-2">{t("patient.alerts.none", "Sin alertas registradas")}</p>
            ) : (
                <div className="flex flex-col items-center gap-3">
                    <div className="w-full max-w-[160px]">
                        <Chart options={options} series={series} type="donut" width="100%" />
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {counts.critical > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                                <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />
                                {counts.critical} {t("patient.alerts.critical", "Críticas")}
                            </span>
                        )}
                        {counts.medium > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                                <span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0" />
                                {counts.medium} {t("patient.alerts.medium", "Medias")}
                            </span>
                        )}
                        {counts.low > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
                                <span className="w-2 h-2 rounded-full bg-brand-400 shrink-0" />
                                {counts.low} {t("patient.alerts.low", "Bajas")}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function PendingNotesCard({ patientId, onViewAll }: { patientId: string; onViewAll: () => void }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [pendingCount, setPendingCount] = useState(0);
    const [totalShared, setTotalShared] = useState(0);

    useEffect(() => {
        listPatientNotes(patientId)
            .then((data) => {
                const shared = data.filter((n) => n.shared_with_therapist);
                const unread = shared.filter((n) => !n.read_by_therapist_at);
                setTotalShared(shared.length);
                setPendingCount(unread.length);
            })
            .finally(() => setLoading(false));
    }, [patientId]);

    return (
        <div className="glass-effect glass-border border rounded-2xl p-5 shadow-theme-sm flex flex-col">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                        <StickyNote className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {t("patient.patientNotes.title", "Notas")}
                    </h3>
                </div>
                <button
                    onClick={onViewAll}
                    className="flex items-center gap-0.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:gap-1 transition-all"
                >
                    {t("common.viewAll")}
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>

            {loading ? (
                <div className="space-y-2 py-2">
                    <div className="h-8 bg-gray-100 dark:bg-white/5 rounded-xl animate-pulse" />
                    <div className="h-4 bg-gray-100 dark:bg-white/5 rounded animate-pulse w-2/3" />
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-4 gap-1">
                    <span className={`text-5xl font-bold tabular-nums ${pendingCount > 0 ? "text-amber-500" : "text-gray-300 dark:text-gray-600"}`}>
                        {pendingCount}
                    </span>
                    <p className="text-xs text-content-tertiary text-center">
                        {t("patient.pendingNotes", "sin leer")}
                    </p>
                    {totalShared > 0 && (
                        <p className="text-xs text-content-tertiary mt-1">
                            {totalShared} {t("patient.sharedNotes", "compartidas en total")}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export default function PatientViewOverview({
    patient,
    onRefresh,
    onTabChange,
    onDelete,
    deleting
}: PatientViewOverviewProps) {
    const { t } = useTranslation();
    const { isPremium } = useSubscription();

    return (
        <div className="space-y-8">
            {/* Last session summary */}
            {isPremium && patient.id && (
                <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-md bg-gradient-to-br from-white/80 to-brand-50/30 dark:from-white/[0.02] dark:to-brand-500/5">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 flex items-center justify-center">
                            <Sparkles className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-content-primary">
                            {t("patient.lastSessionSummary")}
                        </h3>
                    </div>
                    <div className="min-h-[100px]">
                        <RecommendationsCard patientId={patient.id} hideHeader={true} summaryOnly={true} />
                    </div>
                    <div className="mt-4 pt-4 border-t border-brand-100 dark:border-white/5 flex justify-end">
                        <button
                            onClick={() => onTabChange("recommendations")}
                            className="text-brand-600 font-bold text-sm flex items-center gap-1 hover:gap-1.5 transition-all"
                        >
                            {t("patient.viewRecommendations")}
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Row 1: Next scheduled session, when the Scheduler is adopted.
                Kept out of the stats grid below because that row is gated on
                the premium plan and this is gated on tool adoption — folding
                them together would hide one behind the other's condition. */}
            {patient.id && (
                <PatientNextSession
                    kind="patient"
                    id={patient.id}
                    label={`${patient.first_name} ${patient.last_name}`.trim()}
                />
            )}

            {/* Row 2: Stats cards */}
            {isPremium && patient.id && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <AlertTypesPieCard patientId={patient.id} onViewAll={() => onTabChange("alerts")} />
                    <PatientAgreementsSummaryCard
                        patientId={patient.id}
                        onViewAll={() => onTabChange("patientAgreements")}
                    />
                    <PendingNotesCard patientId={patient.id} onViewAll={() => onTabChange("patientNotes")} />
                </div>
            )}

            {/* Row 2: Patient details */}
            <PatientDetails
                therapistID=""
                patient={patient}
                onSuccess={onRefresh}
                onDelete={onDelete}
                deleting={deleting}
            />

            {/* Who they are, as the therapist learns it; the analysis proposes, the therapist decides.
                A training centre's students (enrolled in a course) have no such profile. */}
            {patient.id && !patient.course_id && <PatientIntakeProfile patientId={patient.id} ageFromBirthDate={getPatientAge(patient)} />}

            {/* Only a patient a centre holds has the centre's own fields to fill. */}
            {patient.id && patient.center_id && <PatientCustomFields patientId={patient.id} />}
        </div>
    );
}
